import {
  FETCH_ADMIN_USERS,
  FETCH_ADMIN_USERS_SILENT,
  FETCH_USER_ACCOUNT_ASSIGNMENTS,
  UPDATE_USER_ACCOUNT_ASSIGNMENTS,
  FETCH_AVAILABLE_ACCOUNTS,
  UPDATE_USER_ROLES_PER_ACCOUNT,
  FETCH_ACCOUNT_USERS,
  SET_SELECTED_ADMIN_USER,
} from '../../actions/multiAccountUser';
import {
  AdminUser,
  UserAccountAssignment,
  AccountUserListItem,
} from '../../../types/multiAccountUser';
import { AvailableAccount } from '../../../types/account';

export type MultiAccountUserState = {
  adminUsers: AdminUser[];
  fetchingAdminUsers: boolean;

  selectedAdminUserId: number | null;
  userAccountAssignments: { [userId: number]: UserAccountAssignment[] };
  fetchingUserAssignments: boolean;
  userAssignmentsError?: string;

  updatingAccountAssignments: boolean;
  accountAssignmentsUpdateError?: string;

  availableAccounts: AvailableAccount[];
  fetchingAvailableAccounts: boolean;
  availableAccountsError?: string;

  updatingUserRoles: boolean;
  userRolesUpdateError?: string;

  accountUsers: { [accountId: number]: AccountUserListItem[] };
  accountUsersAccountName: { [accountId: number]: string };
  fetchingAccountUsers: boolean;
  accountUsersError?: string;
};

const initialState: MultiAccountUserState = {
  adminUsers: [],
  fetchingAdminUsers: false,

  selectedAdminUserId: null,
  userAccountAssignments: {},
  fetchingUserAssignments: false,
  userAssignmentsError: undefined,

  updatingAccountAssignments: false,
  accountAssignmentsUpdateError: undefined,

  availableAccounts: [],
  fetchingAvailableAccounts: false,
  availableAccountsError: undefined,

  updatingUserRoles: false,
  userRolesUpdateError: undefined,

  accountUsers: {},
  accountUsersAccountName: {},
  fetchingAccountUsers: false,
  accountUsersError: undefined,
};

const MultiAccountUserReducer = (
  state: MultiAccountUserState = initialState,
  action: any
): MultiAccountUserState => {
  switch (action.type) {
    case `${FETCH_ADMIN_USERS}_PENDING`:
      return {
        ...state,
        fetchingAdminUsers: true,
      };

    case `${FETCH_ADMIN_USERS}_FULFILLED`:
      return {
        ...state,
        fetchingAdminUsers: false,
        adminUsers: action.payload.data.users,
      };

    case `${FETCH_ADMIN_USERS}_REJECTED`:
      return {
        ...state,
        fetchingAdminUsers: false,
      };

    // Silent fetch cases - don't set loading state to avoid unmounting the form
    case `${FETCH_ADMIN_USERS_SILENT}_PENDING`:
      return state;

    case `${FETCH_ADMIN_USERS_SILENT}_FULFILLED`:
      return {
        ...state,
        adminUsers: action.payload.data.users,
      };

    case `${FETCH_ADMIN_USERS_SILENT}_REJECTED`:
      return state;

    case SET_SELECTED_ADMIN_USER:
      return {
        ...state,
        selectedAdminUserId: action.payload,
      };

    case `${FETCH_USER_ACCOUNT_ASSIGNMENTS}_PENDING`:
      return {
        ...state,
        fetchingUserAssignments: true,
        userAssignmentsError: undefined,
      };

    case `${FETCH_USER_ACCOUNT_ASSIGNMENTS}_FULFILLED`:
      return {
        ...state,
        fetchingUserAssignments: false,
        userAccountAssignments: {
          ...state.userAccountAssignments,
          [action.meta.userId]: action.payload.data.account_assignments,
        },
      };

    case `${FETCH_USER_ACCOUNT_ASSIGNMENTS}_REJECTED`:
      return {
        ...state,
        fetchingUserAssignments: false,
        userAssignmentsError:
          action.payload.response?.data?.message || 'Failed to fetch user account assignments',
      };

    case `${UPDATE_USER_ACCOUNT_ASSIGNMENTS}_PENDING`:
      return {
        ...state,
        updatingAccountAssignments: true,
        accountAssignmentsUpdateError: undefined,
      };

    case `${UPDATE_USER_ACCOUNT_ASSIGNMENTS}_FULFILLED`:
      return {
        ...state,
        updatingAccountAssignments: false,
      };

    case `${UPDATE_USER_ACCOUNT_ASSIGNMENTS}_REJECTED`:
      return {
        ...state,
        updatingAccountAssignments: false,
        accountAssignmentsUpdateError:
          action.payload.response?.data?.message || 'Failed to update user account assignments',
      };

    case `${FETCH_AVAILABLE_ACCOUNTS}_PENDING`:
      return {
        ...state,
        fetchingAvailableAccounts: true,
        availableAccountsError: undefined,
      };

    case `${FETCH_AVAILABLE_ACCOUNTS}_FULFILLED`:
      return {
        ...state,
        fetchingAvailableAccounts: false,
        availableAccounts: action.payload.data.accounts,
      };

    case `${FETCH_AVAILABLE_ACCOUNTS}_REJECTED`:
      return {
        ...state,
        fetchingAvailableAccounts: false,
        availableAccountsError:
          action.payload.response?.data?.message || 'Failed to fetch available accounts',
      };

    case `${UPDATE_USER_ROLES_PER_ACCOUNT}_PENDING`:
      return {
        ...state,
        updatingUserRoles: true,
        userRolesUpdateError: undefined,
      };

    case `${UPDATE_USER_ROLES_PER_ACCOUNT}_FULFILLED`:
      return {
        ...state,
        updatingUserRoles: false,
      };

    case `${UPDATE_USER_ROLES_PER_ACCOUNT}_REJECTED`:
      return {
        ...state,
        updatingUserRoles: false,
        userRolesUpdateError:
          action.payload.response?.data?.message || 'Failed to update user roles',
      };

    case `${FETCH_ACCOUNT_USERS}_PENDING`:
      return {
        ...state,
        fetchingAccountUsers: true,
        accountUsersError: undefined,
      };

    case `${FETCH_ACCOUNT_USERS}_FULFILLED`:
      return {
        ...state,
        fetchingAccountUsers: false,
        accountUsers: {
          ...state.accountUsers,
          [action.meta.accountId]: action.payload.data.users,
        },
        accountUsersAccountName: {
          ...state.accountUsersAccountName,
          [action.meta.accountId]: action.payload.data.account_name,
        },
      };

    case `${FETCH_ACCOUNT_USERS}_REJECTED`:
      return {
        ...state,
        fetchingAccountUsers: false,
        accountUsersError:
          action.payload.response?.data?.message || 'Failed to fetch account users',
      };

    default:
      return state;
  }
};

export default MultiAccountUserReducer;
